import {announce} from '@react-aria/live-announcer'
import {create} from 'mutative'

import {useAccountInformationValue} from '@/auth/hooks/useAccountInformation'
import {
  type BookmarkData,
  getBookmarkObserveQueryOptions,
  getBookmarkQueryKey,
} from '@/lib/bookmark/hooks/useBookmark'
import {useLocale} from '@/lib/locale/locale'
import {type BookmarkedProduct, hashProduct, type Product} from '@/models/productModel'
import {bookmark} from '@/services/bookmarkService'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'

export default deepMemo(function ProductBookmarker({
  product,
  className,
  activeClassName,
  children,
}: PropsWithChildren<{
  product: Product
  className?: string
  activeClassName?: string
}>) {
  const queryClient = useQueryClient()
  const [locale] = useLocale()
  const accountInformation = useAccountInformationValue()

  const {data: isBookmarked} = useQuery(
    getBookmarkObserveQueryOptions(
      {
        locale,
        accountInformation,
      },
      {
        select: useCallback(
          (bookmark: BookmarkData) => {
            return bookmark.has(hashProduct(product))
          },
          [product],
        ),
      },
    ),
  )

  interface addOrRemoveBookmarkVariables {
    retailer: Product['retailer']
    id: Product['id']
    isRemove?: boolean
  }

  const {mutate: addOrRemoveBookmark} = useMutation({
    mutationFn: async (variables: addOrRemoveBookmarkVariables) =>
      bookmark({
        locale,
        retailer: variables.retailer,
        id: variables.id,
        isRemove: variables.isRemove,
      }),
    onMutate: async variables => {
      await queryClient.cancelQueries({
        queryKey: getBookmarkQueryKey({locale, accountInformation}),
      })

      const previous = queryClient.getQueryData<BookmarkData>(
        getBookmarkQueryKey({locale, accountInformation}),
      )

      // Optimistically update to the new value
      await queryClient.setQueryData(
        getBookmarkQueryKey({locale, accountInformation}),
        (bookmark: BookmarkData | undefined) => {
          if (variables.isRemove) {
            return create(bookmark ?? new Map<string, BookmarkedProduct>(), draft => {
              draft.delete(hashProduct(variables))
            })
          }
          return create(bookmark ?? new Map<string, BookmarkedProduct>(), draft => {
            draft.set(hashProduct(product), {
              ...product,
              creationTime: new Date(),
            })
          })
        },
      )

      // Return a context object with the previous value so it can be rolled back when the mutation fails.
      return {
        previous,
      }
    },
    onSuccess: (data, variables) => {
      if (variables.isRemove) {
        announce(`Product removed from bookmarks: ${product.title}`)
      } else {
        announce(`Product added to bookmarks: ${product.title}`)
      }
    },
    onError: (err, variables, context) => {
      console.log('??????')
      toastErrorMessage(err.message)
      queryClient.setQueryData(
        getBookmarkQueryKey({locale, accountInformation}),
        () => context?.previous ?? new Map(),
      )
    },
  })

  return (
    <button
      className={clsx(className, isBookmarked && activeClassName)}
      aria-label={
        isBookmarked ? `Remove from bookmark ${product.title}` : `Bookmark ${product.title}`
      }
      onClick={() => {
        addOrRemoveBookmark({
          retailer: product.retailer,
          id: product.id,
          isRemove: isBookmarked,
        })
      }}
    >
      {children}
    </button>
  )
})
